import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Slider,
    Divider,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Radio,
    TextField,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import type { PoplachConfig, PoplachPreset, MapMode, MapaLayout, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';
import { PRESETS } from '../api/JsdhNotifikaceService/generated';
import OdpovediStylePreview from './OdpovediStylePreview';

export const PRESET_LABELS: Record<PoplachPreset, string> = {
    standard: 'Standardní',
    technika: 'Technika',
    'technika-ucast': 'Technika + Účast',
    'vyjezd-2026': 'Výjezd – rozšířený',
    custom: 'Vlastní',
};

export const MAP_MODE_LABELS: Record<MapMode, string> = {
    auto: 'Automatický',
    fixed: 'Fixní přiblížení',
    cycle: 'Přepínání mezi dvěma úrovněmi',
};

export const MAPA_LAYOUT_LABELS: Record<MapaLayout, string> = {
    misto: 'Mapa místa',
    trasa: 'Trasa',
    'misto-trasa': 'Místo + Trasa',
    off: 'Bez mapy',
};

export const DISPLEJ_STYL_LABELS: Record<DisplejStylOdpovedi, string> = {
    STRIPE: 'Proužek vlevo (klasický)',
    BACKGROUND_TINTED: 'Pastelové pozadí karet',
};

export const DISPLEJ_STYL_DESCRIPTIONS: Record<DisplejStylOdpovedi, string> = {
    STRIPE: 'Kompaktní karty s barevným proužkem vlevo. JEDU vlevo, NEJEDU vpravo.',
    BACKGROUND_TINTED: 'Pastelové karty s velkým counter chipem na okrajích. JEDU vlevo, NEJEDU vpravo.',
};

export const ZOOM_MARKS = Array.from({ length: 10 }, (_, i) => ({ value: i + 10, label: `${i + 10}` }));

export const INTERVAL_OPTIONS = [
    { value: 2000, label: '2s' },
    { value: 2500, label: '2.5s' },
    { value: 3000, label: '3s' },
    { value: 3500, label: '3.5s' },
    { value: 4000, label: '4s' },
    { value: 5000, label: '5s' },
    { value: 7000, label: '7s' },
];

export interface PoplachConfigFormProps {
    config: PoplachConfig;
    onChange: (updater: (prev: PoplachConfig) => PoplachConfig) => void;
}

const PoplachConfigForm: React.FC<PoplachConfigFormProps> = ({ config, onChange }) => {
    const handlePresetChange = (_: React.MouseEvent<HTMLElement>, preset: PoplachPreset | null) => {
        if (!preset) return;
        onChange(prev => ({
            ...PRESETS[preset],
            mapaLayout: prev.mapaLayout,
            mapa: prev.mapa,
            ucast: prev.ucast,
            displejStylOdpovedi: prev.displejStylOdpovedi,
            displejAdaptivniLayout: prev.displejAdaptivniLayout,
            // Vědomá volba presetu = trvale fixujeme proti další auto-migraci v parsePoplachConfig.
            _presetMigratedV2: true,
        }));
    };

    const handleMapModeChange = (mode: MapMode) => {
        onChange(prev => ({
            ...prev,
            mapa: { ...prev.mapa, mode, fixedZoom: prev.mapa?.fixedZoom ?? 15, zoom1: prev.mapa?.zoom1 ?? 15, zoom2: prev.mapa?.zoom2 ?? 17, interval: prev.mapa?.interval ?? 3500 },
        }));
    };

    const handleMapConfigChange = (field: string, value: number) => {
        onChange(prev => ({
            ...prev,
            mapa: { ...prev.mapa!, [field]: value },
        }));
    };

    const handleMapaLayoutChange = (_: React.MouseEvent<HTMLElement>, layout: MapaLayout | null) => {
        if (!layout) return;
        onChange(prev => ({
            ...prev,
            mapaLayout: layout,
            sections: { ...prev.sections, mapa: layout !== 'off' },
        }));
    };

    return (
        <Box sx={{ pt: 1 }}>
            {/* Preset selection */}
            <Typography variant="subtitle2" gutterBottom>
                Preset
            </Typography>
            <ToggleButtonGroup
                value={config.preset}
                exclusive
                onChange={handlePresetChange}
                size="small"
                sx={{ mb: 2 }}
            >
                <ToggleButton value="vyjezd-2026">Výjezd – rozšířený</ToggleButton>
                <ToggleButton value="standard">Standardní</ToggleButton>
                <ToggleButton value="technika">Technika</ToggleButton>
                <ToggleButton value="technika-ucast">Technika + Účast</ToggleButton>
            </ToggleButtonGroup>

            {/* Map layout selection */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
                Zobrazení mapy
            </Typography>
            <ToggleButtonGroup
                value={config.mapaLayout || 'misto'}
                exclusive
                onChange={handleMapaLayoutChange}
                size="small"
                sx={{ mb: 2, flexWrap: 'wrap' }}
            >
                {(Object.entries(MAPA_LAYOUT_LABELS) as [MapaLayout, string][]).map(([value, label]) => (
                    <ToggleButton key={value} value={value}>{label}</ToggleButton>
                ))}
            </ToggleButtonGroup>

            {/* Map zoom configuration - only for layouts with misto map */}
            {(config.mapaLayout === 'misto' || config.mapaLayout === 'misto-trasa') && (
                <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                        Režim zoomu
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Režim</InputLabel>
                        <Select
                            value={config.mapa?.mode || 'auto'}
                            label="Režim"
                            onChange={(e) => handleMapModeChange(e.target.value as MapMode)}
                        >
                            {Object.entries(MAP_MODE_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {config.mapa?.mode === 'fixed' && (
                        <Box sx={{ px: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Přiblížení: {config.mapa.fixedZoom ?? 15}
                            </Typography>
                            <Slider
                                value={config.mapa.fixedZoom ?? 15}
                                onChange={(_, v) => handleMapConfigChange('fixedZoom', v as number)}
                                min={10}
                                max={19}
                                step={1}
                                marks={ZOOM_MARKS}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    )}

                    {config.mapa?.mode === 'cycle' && (
                        <Box sx={{ px: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Zoom 1: {config.mapa.zoom1 ?? 15}
                            </Typography>
                            <Slider
                                value={config.mapa.zoom1 ?? 15}
                                onChange={(_, v) => handleMapConfigChange('zoom1', v as number)}
                                min={10}
                                max={19}
                                step={1}
                                marks={ZOOM_MARKS}
                                valueLabelDisplay="auto"
                            />
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                                Zoom 2: {config.mapa.zoom2 ?? 17}
                            </Typography>
                            <Slider
                                value={config.mapa.zoom2 ?? 17}
                                onChange={(_, v) => handleMapConfigChange('zoom2', v as number)}
                                min={10}
                                max={19}
                                step={1}
                                marks={ZOOM_MARKS}
                                valueLabelDisplay="auto"
                            />
                            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                                <InputLabel>Interval přepínání</InputLabel>
                                <Select
                                    value={config.mapa.interval ?? 3500}
                                    label="Interval přepínání"
                                    onChange={(e) => handleMapConfigChange('interval', e.target.value as number)}
                                >
                                    {INTERVAL_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </>
            )}

            {/* Zobrazení — obsahuje jen kontrolky relevantní pro daný preset.
                Mapování (kdo co konzumuje):
                  - casVyhlaseni: čte DetailPoplachu (standard/technika/technika-ucast).
                                  vyjezd-2026 má vždy CasovkaOverlay v mapě → kontrolka skryta.
                  - datumCasVyhlaseni: čte OdpovediWrapper → jen standard.
                  - zobrazitSpecializace: čte OdpovediWrapper (standard) i Vyjezd2026OdpovediSection.
                                  technika nemá odpovědi; technika-ucast má vlastní ucast.specializace.
                  - zobrazitFunkce: čte Vyjezd2026OdpovediSection — chip s rolí (VD/S/H) u jména. Jen vyjezd-2026. */}
            {(() => {
                const showCas = config.preset === 'standard' || config.preset === 'technika' || config.preset === 'technika-ucast';
                const showDatum = config.preset === 'standard';
                const showSpec = config.sections.odpovedi && config.preset !== 'technika-ucast';
                const showFunkce = config.preset === 'vyjezd-2026';
                if (!showCas && !showDatum && !showSpec && !showFunkce) return null;
                return (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            Zobrazení
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                            {showCas && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.casVyhlaseni !== false}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                casVyhlaseni: checked,
                                            }))}
                                        />
                                    }
                                    label="Čas od vyhlášení (odpočet)"
                                />
                            )}
                            {showDatum && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.datumCasVyhlaseni === true}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                datumCasVyhlaseni: checked,
                                            }))}
                                        />
                                    }
                                    label="Datum a čas vyhlášení"
                                />
                            )}
                            {showSpec && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.zobrazitSpecializace !== false}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                zobrazitSpecializace: checked,
                                            }))}
                                        />
                                    }
                                    label="Specializace u odpovědí"
                                />
                            )}
                            {showFunkce && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.zobrazitFunkce !== false}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                zobrazitFunkce: checked,
                                            }))}
                                        />
                                    }
                                    label="Funkce u odpovědí (VD/S/H)"
                                />
                            )}
                        </Box>
                    </>
                );
            })()}

            {/* Zobrazení odpovědí na displeji — jen presetu, jejichž odpovědi se renderují přes
                OdpovediWrapper / TechnikaUcastWrapper a respektují displejStylOdpovedi + displejAdaptivniLayout.
                vyjezd-2026 má vlastní hardcoded vzhled (BACKGROUND_TINTED + adaptivní), proto se kontrolka
                pro něj neukazuje. technika preset nemá odpovědi vůbec. */}
            {(config.preset === 'standard' || config.preset === 'technika-ucast') && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Zobrazení odpovědí na displeji
                    </Typography>
                    <Box
                        sx={{
                            ml: 1,
                            mb: 1,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
                            columnGap: 2,
                            rowGap: 1,
                            alignItems: 'center',
                        }}
                    >
                        {(Object.entries(DISPLEJ_STYL_LABELS) as [DisplejStylOdpovedi, string][]).flatMap(([value, label]) => {
                            const selected = (config.displejStylOdpovedi || 'STRIPE') === value;
                            return [
                                <FormControlLabel
                                    key={`${value}-label`}
                                    value={value}
                                    control={
                                        <Radio
                                            size="small"
                                            checked={selected}
                                            onChange={() => onChange(prev => ({
                                                ...prev,
                                                displejStylOdpovedi: value,
                                            }))}
                                        />
                                    }
                                    sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.5 } }}
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{label}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                {DISPLEJ_STYL_DESCRIPTIONS[value]}
                                            </Typography>
                                        </Box>
                                    }
                                />,
                                <OdpovediStylePreview
                                    key={`${value}-preview`}
                                    displejStyl={value}
                                    showSpecializace={config.zobrazitSpecializace !== false}
                                    adaptivniLayout={config.displejAdaptivniLayout === true}
                                />,
                            ];
                        })}
                    </Box>
                    <Box sx={{ ml: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.displejAdaptivniLayout === true}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        displejAdaptivniLayout: checked,
                                    }))}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Adaptivní layout — větší karty při méně členech
                                    <Tooltip
                                        title="Sekce s ≤ 4 členy se zvětší (větší font, 1 sloupec). JEDU a NEJEDU se vyhodnocují samostatně — když má JEDU 3 členy a NEJEDU 7, zvětší se jen JEDU."
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />
                    </Box>
                </>
            )}

            {/* Pravidla výjezdu — jen pro preset vyjezd-2026 */}
            {config.preset === 'vyjezd-2026' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Pravidla výjezdu
                        <Tooltip title="Když jsou pravidla zapnutá, semafor zobrazí 'plombu' (KOMPLETNÍ / CHYBÍ VELITEL / CHYBÍ STROJNÍK / MÁLO LIDÍ) podle toho, jestli odpovědi vyhovují minimálním podmínkám." arrow>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1, gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.pravidlaVyjezdu?.enabled === true}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        pravidlaVyjezdu: {
                                            ...(prev.pravidlaVyjezdu ?? { minPocet: 4, vyzadovatVelitele: true, vyzadovatStrojnika: true }),
                                            enabled: checked,
                                        } as any,
                                    }))}
                                />
                            }
                            label="Vyhodnocovat pravidla výjezdu"
                        />
                        {config.pravidlaVyjezdu?.enabled && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 4 }}>
                                <TextField
                                    label="Minimální počet hasičů"
                                    type="number"
                                    size="small"
                                    value={config.pravidlaVyjezdu?.minPocet ?? 4}
                                    onChange={(e) => {
                                        const n = parseInt(e.target.value, 10);
                                        onChange(prev => ({
                                            ...prev,
                                            pravidlaVyjezdu: {
                                                ...(prev.pravidlaVyjezdu ?? { enabled: true, vyzadovatVelitele: true, vyzadovatStrojnika: true }),
                                                minPocet: Number.isNaN(n) ? 0 : Math.max(0, n),
                                            } as any,
                                        }));
                                    }}
                                    inputProps={{ min: 0, max: 20 }}
                                    sx={{ width: 200 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.pravidlaVyjezdu?.vyzadovatVelitele === true}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                pravidlaVyjezdu: {
                                                    ...(prev.pravidlaVyjezdu ?? { enabled: true, minPocet: 4, vyzadovatStrojnika: true }),
                                                    vyzadovatVelitele: checked,
                                                } as any,
                                            }))}
                                        />
                                    }
                                    label="Vyžadovat velitele družstva (VD)"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.pravidlaVyjezdu?.vyzadovatStrojnika === true}
                                            onChange={(_e, checked) => onChange(prev => ({
                                                ...prev,
                                                pravidlaVyjezdu: {
                                                    ...(prev.pravidlaVyjezdu ?? { enabled: true, minPocet: 4, vyzadovatVelitele: true }),
                                                    vyzadovatStrojnika: checked,
                                                } as any,
                                            }))}
                                        />
                                    }
                                    label="Vyžadovat strojníka (S)"
                                />
                            </Box>
                        )}
                        <FormControl size="small" sx={{ minWidth: 240, mt: 1 }}>
                            <InputLabel>Noční režim</InputLabel>
                            <Select
                                value={config.nocniRezim ?? 'auto'}
                                label="Noční režim"
                                onChange={(e) => onChange(prev => ({
                                    ...prev,
                                    nocniRezim: e.target.value as 'auto' | 'light' | 'dark',
                                }))}
                            >
                                <MenuItem value="auto">Automaticky (noční mód 20:00–6:00)</MenuItem>
                                <MenuItem value="light">Vždy denní</MenuItem>
                                <MenuItem value="dark">Vždy noční</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </>
            )}

            {/* Karta účasti — jen pro preset technika-ucast */}
            {config.preset === 'technika-ucast' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Karta účasti
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.ucast?.semafor !== false}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        ucast: { ...prev.ucast, semafor: checked },
                                    }))}
                                />
                            }
                            label="Počítadlo (semafor)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.ucast?.role !== false}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        ucast: { ...prev.ucast, role: checked },
                                    }))}
                                />
                            }
                            label="Role (VD, S, H)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.ucast?.specializace !== false}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        ucast: { ...prev.ucast, specializace: checked },
                                    }))}
                                />
                            }
                            label="Specializace"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.ucast?.responzivni !== false}
                                    onChange={(_e, checked) => onChange(prev => ({
                                        ...prev,
                                        ucast: { ...prev.ucast, responzivni: checked },
                                    }))}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Responzivní karta účasti
                                    <Tooltip title="Při méně než 2 technikách se karta účasti roztáhne do volného prostoru a zobrazí jména ve více sloupcích" arrow>
                                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PoplachConfigForm;
